import { ChatIcon, EditIcon, EmailIcon, SettingsIcon } from '@chakra-ui/icons';
import { Box, Button, Checkbox, CheckboxGroup, Flex, HStack, Icon, Spacer, Stack, Text, Tooltip } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { QueryKey } from '@tanstack/react-query';
import { Activity, ActivityProperties, User } from 'api/types';
import { useUserContext } from 'context/UserContextProvider';
import { dateTimeTransformer, dateTransformerNoTimezone } from 'helpers/dateTransformer';
import { find, get } from 'lodash';
import { ReactNode, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FiArrowRight, FiBarChart2 } from 'react-icons/fi';
import { IoTrash } from 'react-icons/io5';
import { MdOutlineHdrAuto } from 'react-icons/md';
import { RiMoneyEuroCircleLine } from 'react-icons/ri';
import * as yup from 'yup';

import { textareaInput } from '../helpers/makeFormFields';
import useDeleteActivityLogMessageMutation from '../hooks/mutations/useDeleteActivityLogMessageMutation';
import useSendActivityLogMessageMutation from '../hooks/mutations/useSendActivityLogMessageMutation';
import { Card } from '../layout/Card';

export interface IMessageForm {
  text: string;
}

const colorMap = {
  comment: 'green.200',
  op_api: 'blue.400',
  b2c_api: 'black.200',
  cp_api: 'orange.200',
  mail: 'purple.200',
  auditing: 'gray.200',
  spf_api: 'red.200',
  automation: 'yellow.200',
};

const ActivityLogCard = (props: {
  account_id?: number;
  activities: Array<Activity> | undefined;
  canSendMessage?: boolean;
  footer?: ReactNode;
  queryKey: QueryKey;
  resourceKey: string;
  showSubjectName?: boolean;
  workshopName?: string;
  m?: number;
}) => {
  const {
    account_id,
    activities = [],
    canSendMessage = true,
    footer,
    queryKey,
    resourceKey,
    showSubjectName = false,
    workshopName = '',
    m = 'inherit',
  } = props;
  const { user, opUsers } = useUserContext();
  const logTypes = ['comment', 'op_api', 'cp_api', 'mail', 'auditing', 'spf_api', 'automation', 'b2c_api'];
  const [logType, setLogType] = useState(['comment', 'op_api', 'cp_api', 'mail', 'spf_api', 'automation', 'b2c_api']);

  const { t } = useTranslation();
  const schema = yup
    .object({
      text: yup.string().max(2000, t('activity:message.max_characters')).required(),
    })
    .required();

  const { statusCodes } = useUserContext();
  // @ts-ignore
  const statuses = statusCodes?.[resourceKey];
  // @ts-ignore
  const reasons = statusCodes?.[`${resourceKey}` + '_reasons'];

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<IMessageForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
  const deleteMessageMutation = useDeleteActivityLogMessageMutation(queryKey);
  const { mutate: sendMessage, isPending } = useSendActivityLogMessageMutation(reset, setError, queryKey);
  const onSubmit: SubmitHandler<IMessageForm> = (text) => {
    if (account_id) {
      sendMessage({
        id: String(account_id),
        text,
        resourceKey: resourceKey?.replace('_', '-'),
      });
    }
  };

  const getProcessedProperties = (eventType: string, properties: ActivityProperties): ActivityProperties => {
    if (eventType === 'assigned' || eventType === 'reassigned' || eventType === 'automation_assigned') {
      const from = properties?.from ? (properties?.from == 'automation' ? 'automation' : find(opUsers, ['id', properties?.from])?.name) : undefined;
      const to = find(opUsers, ['id', properties?.to])?.name;

      return {
        from,
        to,
      };
    }
    if (eventType === 'workshop.updated') {
      const assignee = properties?.assignee ? find(opUsers, ['id', properties?.assignee])?.name : undefined;

      return {
        assignee,
      };
    }
    if (eventType === 'subscription.extend_trial') {
      const at = properties?.to ? dateTransformerNoTimezone(properties.to.toString()) : undefined;
      return {
        to: at,
      };
    }
    if (eventType === 'status_change') {
      if (typeof properties.from === 'number') {
        const reasonName = properties.reason != null && properties.to != null ? get(reasons, properties.to, {})[properties.reason] : undefined;
        return {
          from: properties?.from != null ? t(`${resourceKey}:status.${get(statuses, properties.from)}.label`) : undefined,
          to: properties?.to != null ? t(`${resourceKey}:status.${get(statuses, properties.to)}.label`) : undefined,
          reason:
            reasonName != null && properties?.to != null ? t(`${resourceKey}:status.${get(statuses, properties.to)}.${reasonName}.label`) : undefined,
        };
      } else {
        return {
          from: properties?.from != null ? t(`${resourceKey}:status.${properties.from}.label`) : undefined,
          to: properties?.to != null ? t(`${resourceKey}:status.${properties.to}.label`) : undefined,
        };
      }
    }

    return properties;
  };

  const [isInputActive, setIsInputActive] = useState(false);

  const filteredActivities = logType.length === 0 ? activities : activities.filter((obj) => logType.includes(obj.log_name));

  return (
    <Card
      m={m}
      px={4}
      py={8}
      position={'sticky'}
      top={{ base: 3, lg: 6 }}
      zIndex={10}
      borderRadius="lg"
      boxShadow={isInputActive ? 'md' : '2xl'}
      transition={'box-shadow .2s ease-in'}
    >
      <Stack spacing={{ base: 3, lg: 6 }} px={4}>
        <CheckboxGroup data-test-id="auditing-filter" defaultValue={logType} onChange={(value: any) => setLogType(value)}>
          <Stack spacing={[1, 5]} flexWrap={'wrap'} direction={['column', 'row']}>
            {logTypes.map((type) => {
              return (
                <Checkbox key={type} value={type} data-test-id={type}>
                  {t(`activity:${type}.label`)}
                </Checkbox>
              );
            })}
          </Stack>
        </CheckboxGroup>
        <Stack direction={'column'} spacing={2} overflow={'auto'}>
          {filteredActivities.map((activity: Activity, index: number) => {
            const statusReason = getProcessedProperties(activity?.description, activity?.properties)?.reason as string;
            const statusReasonIsUndefined = statusReason?.includes('undefined');
            const opApiStatusChangeWithoutReason =
              activity.log_name === 'op_api' &&
              activity?.description === 'status_change' &&
              !getProcessedProperties(activity?.description, activity?.properties)?.reason;
            const automationStatusChangeWithoutReason =
              activity.log_name === 'automation' &&
              activity?.description === 'status_change' &&
              !getProcessedProperties(activity?.description, activity?.properties)?.reason;
            const automationFileUrls =
              activity.log_name === 'automation' &&
              (activity?.description?.includes('completed') ||
                activity?.description?.includes('detected') ||
                activity?.description?.includes('canceled') ||
                activity?.description?.includes('rejected') ||
                activity?.description?.includes('failed') ||
                activity?.description?.includes('cancelled')) &&
              activity?.attached_files;

            // @ts-ignore
            const causer: User = find(opUsers, ['id', activity?.causer_id]);
            let icon = null;
            let causerName = t('activity:causer.' + activity?.log_name);
            switch (activity?.log_name) {
              case 'cp_api':
                causerName = workshopName;
                icon = <FiBarChart2 size={'22'} />;
                break;
              case 'op_api':
                causerName = causer?.name;
                icon = <EditIcon boxSize={'5'} />;
                break;
              case 'b2c_api':
                causerName = causer?.name;
                icon = <EditIcon boxSize={'5'} />;
                break;
              case 'comment':
                causerName = causer?.name;
                icon = <ChatIcon boxSize={'5'} />;
                break;
              case 'mail':
                causerName = 'Mail';
                icon = <EmailIcon boxSize={'5'} />;
                break;
              case 'spf_api':
                causerName = t('activity:spf_api.label');
                icon = <RiMoneyEuroCircleLine size={'25'} />;
                break;
              case 'automation':
                causerName = t('activity:automation.label');
                icon = <MdOutlineHdrAuto size={'25'} />;
                break;
              default:
                icon = <SettingsIcon boxSize={'5'} />;
                break;
            }

            return (
              <Box width={'full'} key={index}>
                <Stack
                  spacing={2}
                  rounded="md"
                  py={{ base: '2', md: '2' }}
                  px={{ base: '2', md: '2' }}
                  bg={get(colorMap, activity?.log_name)}
                  color={'default'}
                >
                  <Box>
                    <HStack color="muted" fontSize="xs" m={0}>
                      <Box>{icon}</Box>
                      <Box>{causerName}</Box>
                      <Spacer />
                      {activity.log_name === 'comment' && causer?.id === user?.id ? (
                        <Tooltip label={t('activity:comment.delete_comment')} shouldWrapChildren>
                          <Button
                            size={'sm'}
                            variant={'link'}
                            colorScheme={'red'}
                            aria-label={'remove file'}
                            leftIcon={<Icon as={IoTrash} boxSize="5" />}
                            onClick={() => {
                              deleteMessageMutation.mutate({
                                id: activity?.id,
                              });
                            }}
                          ></Button>
                        </Tooltip>
                      ) : null}
                    </HStack>

                    <Text color="muted" fontSize="xs" m={0}>
                      {dateTimeTransformer(activity.created_at)}
                    </Text>
                  </Box>
                  {showSubjectName && activity.subject_name && (
                    <Text fontSize="xs" fontWeight="semibold">
                      {activity.subject_name}
                    </Text>
                  )}
                  <Text fontSize="xs">
                    {opApiStatusChangeWithoutReason || automationStatusChangeWithoutReason || statusReasonIsUndefined
                      ? t(
                          `activity:${activity.log_name}.${activity?.description}` + '_no_reason',
                          getProcessedProperties(activity?.description, activity?.properties),
                        )
                      : activity.log_name === 'comment'
                        ? activity?.description
                        : t(
                            `activity:${activity.log_name}.${activity?.description}`,
                            getProcessedProperties(activity?.description, activity?.properties),
                          )}
                    {automationFileUrls ? <br /> : null}
                    {automationFileUrls
                      ? activity?.attached_files?.map((attachedFile, i) => {
                          return (
                            <b key={i}>
                              <a href={attachedFile?.url}>{attachedFile.name}</a>
                              <br />
                            </b>
                          );
                        })
                      : ''}
                  </Text>
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {canSendMessage && (
          <Box as="form" onSubmit={handleSubmit(onSubmit)} data-test-id="upload-form">
            {textareaInput({
              name: 'text',
              placeholder: t('activity:message.placeholder'),
              onFocus: () => {
                setIsInputActive(true);
              },
              trigger: () => setIsInputActive(false),
              register,
              errors,
            })}

            <Flex direction="row-reverse" marginTop={2}>
              <Button type="submit" variant="primary" disabled={!isValid || isPending} data-test-id="message-submit-button">
                {t('common:submit')}
                <Icon as={FiArrowRight} boxSize="4" />
              </Button>
            </Flex>
          </Box>
        )}
      </Stack>
      {footer}
    </Card>
  );
};

export default ActivityLogCard;
